import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  CircularProgress,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import ProjectIcon from '@mui/icons-material/Folder';
import ActivityIcon from '@mui/icons-material/Assignment';
import NoteIcon from '@mui/icons-material/Note';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { notificationsApi } from '../services/api';
import { formatDistanceToNow } from 'date-fns';
import { AppContext } from '../App';

const SUMMARY_POLL_INTERVAL_MS = 60000;
const OPEN_MENU_REFRESH_INTERVAL_MS = 30000;
const LEADER_HEARTBEAT_MS = 15000;
const LEADER_TIMEOUT_MS = 45000;
const NOTIFICATION_LEADER_KEY = 'notifications-polling-leader';
const NOTIFICATION_BROADCAST_KEY = 'notifications-badge-update';

const NotificationsDropdown = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const { currentUser } = useContext(AppContext) || {};
  const previousUnreadCountRef = useRef(0);
  const isInitialLoadRef = useRef(true);
  const tabIdRef = useRef(`notifications-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const summaryIntervalRef = useRef(null);
  const openMenuIntervalRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const broadcastChannelRef = useRef(null);
  const isLeaderRef = useRef(false);
  const isVisibleRef = useRef(typeof document !== 'undefined' ? document.visibilityState === 'visible' : true);

  const open = Boolean(anchorEl);

  const stopSummaryPolling = () => {
    if (summaryIntervalRef.current) {
      window.clearInterval(summaryIntervalRef.current);
      summaryIntervalRef.current = null;
    }
  };

  const stopMenuRefresh = () => {
    if (openMenuIntervalRef.current) {
      window.clearInterval(openMenuIntervalRef.current);
      openMenuIntervalRef.current = null;
    }
  };

  const stopHeartbeat = () => {
    if (heartbeatIntervalRef.current) {
      window.clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  };

  const broadcastUnreadCount = (nextUnreadCount) => {
    const payload = {
      source: tabIdRef.current,
      unreadCount: nextUnreadCount,
      timestamp: Date.now(),
    };

    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage(payload);
    }

    localStorage.setItem(NOTIFICATION_BROADCAST_KEY, JSON.stringify(payload));
  };

  const updateUnreadCount = (nextUnreadCount, { broadcast = false, allowSound = false } = {}) => {
    const previousCount = previousUnreadCountRef.current;
    setUnreadCount(nextUnreadCount);

    if (
      allowSound &&
      !isInitialLoadRef.current &&
      previousCount > 0 &&
      nextUnreadCount > previousCount &&
      currentUser?.play_notification_sound !== false
    ) {
      playNotificationSound();
    }

    previousUnreadCountRef.current = nextUnreadCount;

    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
    }

    if (broadcast) {
      broadcastUnreadCount(nextUnreadCount);
    }
  };

  const readLeaderRecord = () => {
    const leaderRaw = localStorage.getItem(NOTIFICATION_LEADER_KEY);
    if (!leaderRaw) {
      return null;
    }

    try {
      return JSON.parse(leaderRaw);
    } catch {
      return null;
    }
  };

  const writeLeaderRecord = () => {
    localStorage.setItem(NOTIFICATION_LEADER_KEY, JSON.stringify({
      tabId: tabIdRef.current,
      expiresAt: Date.now() + LEADER_TIMEOUT_MS,
    }));
  };

  const releaseLeadership = () => {
    const leader = readLeaderRecord();
    if (leader?.tabId === tabIdRef.current) {
      localStorage.removeItem(NOTIFICATION_LEADER_KEY);
    }
    isLeaderRef.current = false;
    stopSummaryPolling();
    stopHeartbeat();
  };

  const loadNotificationSummary = async ({ allowSound = false, broadcast = false } = {}) => {
    try {
      const response = await notificationsApi.getSummary();
      const nextUnreadCount = response.data?.unread_count ?? 0;
      updateUnreadCount(nextUnreadCount, { broadcast, allowSound });
    } catch (error) {
      console.error('Error loading notification summary:', error);
    }
  };

  const startSummaryPolling = () => {
    if (summaryIntervalRef.current || !isVisibleRef.current) {
      return;
    }

    summaryIntervalRef.current = window.setInterval(() => {
      if (!document.hidden && isLeaderRef.current) {
        loadNotificationSummary({ allowSound: true, broadcast: true });
      }
    }, SUMMARY_POLL_INTERVAL_MS);
  };

  const maintainLeadership = () => {
    writeLeaderRecord();

    if (!heartbeatIntervalRef.current) {
      heartbeatIntervalRef.current = window.setInterval(() => {
        if (isLeaderRef.current && !document.hidden) {
          writeLeaderRecord();
        }
      }, LEADER_HEARTBEAT_MS);
    }

    startSummaryPolling();
  };

  const attemptLeadership = () => {
    if (!isVisibleRef.current) {
      releaseLeadership();
      return;
    }

    const leader = readLeaderRecord();
    const leaderExpired = !leader || leader.expiresAt <= Date.now();
    const leaderIsCurrentTab = leader?.tabId === tabIdRef.current;

    if (leaderExpired || leaderIsCurrentTab) {
      isLeaderRef.current = true;
      maintainLeadership();
      return;
    }

    isLeaderRef.current = false;
    stopSummaryPolling();
    stopHeartbeat();
  };

  // Simple function to play notification sound
  const playNotificationSound = () => {
    if (currentUser?.play_notification_sound !== false) {
      try {
        const audio = new Audio('/ping.mp3');
        audio.volume = 0.5;
        audio.play().catch(e => {
          // Try alternative method if first fails
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          fetch('/ping.mp3')
            .then(response => response.arrayBuffer())
            .then(data => audioContext.decodeAudioData(data))
            .then(buffer => {
              const source = audioContext.createBufferSource();
              source.buffer = buffer;
              source.connect(audioContext.destination);
              source.start();
            })
            .catch(() => console.log('Audio blocked by browser'));
        });
      } catch (e) {
        console.log('Audio not supported');
      }
    }
  };

  useEffect(() => {
    broadcastChannelRef.current = typeof BroadcastChannel !== 'undefined'
      ? new BroadcastChannel('notifications-badge')
      : null;

    const handleIncomingUnreadCount = (payload) => {
      if (!payload || payload.source === tabIdRef.current || typeof payload.unreadCount !== 'number') {
        return;
      }

      setUnreadCount(payload.unreadCount);
      previousUnreadCountRef.current = payload.unreadCount;
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
      }
    };

    const handleStorage = (event) => {
      if (event.key === NOTIFICATION_BROADCAST_KEY && event.newValue) {
        try {
          handleIncomingUnreadCount(JSON.parse(event.newValue));
        } catch {
          // Ignore malformed broadcast payloads.
        }
      }

      if (event.key === NOTIFICATION_LEADER_KEY) {
        attemptLeadership();
      }
    };

    const handleBroadcastMessage = (event) => {
      handleIncomingUnreadCount(event.data);
    };

    const handleVisibilityChange = () => {
      isVisibleRef.current = document.visibilityState === 'visible';

      if (isVisibleRef.current) {
        attemptLeadership();
        loadNotificationSummary({ allowSound: true, broadcast: isLeaderRef.current });
      } else if (isLeaderRef.current) {
        releaseLeadership();
      }
    };

    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.addEventListener('message', handleBroadcastMessage);
    }

    window.addEventListener('storage', handleStorage);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    attemptLeadership();
    loadNotificationSummary({ broadcast: isLeaderRef.current });

    return () => {
      releaseLeadership();
      stopMenuRefresh();
      window.removeEventListener('storage', handleStorage);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.removeEventListener('message', handleBroadcastMessage);
        broadcastChannelRef.current.close();
      }
    };
  }, [currentUser?.play_notification_sound]);

  const loadNotifications = async ({ showSpinner = false } = {}) => {
    if (showSpinner) {
      setNotificationsLoading(true);
    }

    try {
      const response = await notificationsApi.getAll(false, 20);
      setNotifications(response.data);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      if (showSpinner) {
        setNotificationsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!open || document.hidden) {
      stopMenuRefresh();
      return undefined;
    }

    openMenuIntervalRef.current = window.setInterval(() => {
      if (!document.hidden) {
        loadNotifications();
        loadNotificationSummary({ broadcast: isLeaderRef.current });
      }
    }, OPEN_MENU_REFRESH_INTERVAL_MS);

    return () => {
      stopMenuRefresh();
    };
  }, [open]);

  const handleClick = async (event) => {
    // Enable audio context on user interaction
    try {
      const audio = new Audio('/ping.mp3');
      audio.volume = 0;
      audio.play().then(() => audio.pause()).catch(() => {});
    } catch (e) {}

    setAnchorEl(event.currentTarget);

    await Promise.all([
      loadNotifications({ showSpinner: true }),
      loadNotificationSummary({ allowSound: true, broadcast: isLeaderRef.current }),
    ]);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read if not already read
      if (!notification.is_read) {
        await notificationsApi.markAsRead(notification.notification_id);
        
        // Update local state
        setNotifications(prev => 
          prev.map(n => 
            n.notification_id === notification.notification_id 
              ? { ...n, is_read: true }
              : n
          )
        );
        updateUnreadCount(Math.max(0, previousUnreadCountRef.current - 1), { broadcast: true });
      }

      // Navigate to related item if applicable
      if (notification.related_project_id) {
        // If there's a related activity, navigate to the project with activity tab and specific activity expanded
        if (notification.related_activity_id) {
          window.location.href = `/projects/${notification.related_project_id}?tab=activities&activityId=${notification.related_activity_id}`;
        } else {
          window.location.href = `/projects/${notification.related_project_id}`;
        }
      } else if (notification.title === 'Time Entry Queried') {
        window.location.href = '/my-time?focus=challenged';
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
    
    handleClose();
  };

  const handleMarkAllRead = async () => {
    try {
      setLoading(true);
      await notificationsApi.markAllAsRead();
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => ({ ...n, is_read: true }))
      );
      updateUnreadCount(0, { broadcast: true });
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClearNotification = async (notificationId) => {
    try {
      await notificationsApi.delete(notificationId);
      
      // Update local state
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      // Update unread count if the deleted notification was unread
      const deletedNotification = notifications.find(n => n.notification_id === notificationId);
      if (deletedNotification && !deletedNotification.is_read) {
        updateUnreadCount(Math.max(0, previousUnreadCountRef.current - 1), { broadcast: true });
      }
    } catch (error) {
      console.error('Error clearing notification:', error);
    }
  };

  const handleClearAllNotifications = async () => {
    try {
      setLoading(true);
      await notificationsApi.clearAll();
      
      // Update local state
      setNotifications([]);
      updateUnreadCount(0, { broadcast: true });
    } catch (error) {
      console.error('Error clearing all notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const getNotificationIcon = (notification) => {
    if (notification.related_project_id) return <ProjectIcon fontSize="small" />;
    if (notification.related_activity_id) return <ActivityIcon fontSize="small" />;
    if (notification.related_note_id) return <NoteIcon fontSize="small" />;
    return <NotificationsIcon fontSize="small" />;
  };

  const formatNotificationTime = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'Unknown time';
    }
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        aria-label="notifications"
      >
        <Badge badgeContent={unreadCount} color="error">
          {unreadCount > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
        </Badge>
      </IconButton>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 'calc(100vh - 100px)', // Ensure popup fits on screen with margin
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            style: {
              maxHeight: 'calc(100vh - 100px)', // Additional constraint for Material-UI
            }
          }
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Notifications</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {unreadCount > 0 && (
                <Button
                  size="small"
                  onClick={handleMarkAllRead}
                  disabled={loading || notificationsLoading}
                  startIcon={<NotificationsIcon />}
                >
                  Mark all read
                </Button>
              )}
              {notifications.length > 0 && (
                <Button
                  size="small"
                  onClick={handleClearAllNotifications}
                  disabled={loading || notificationsLoading}
                  startIcon={<DeleteSweepIcon />}
                  color="error"
                >
                  Clear all
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        {/* Notifications List */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {notificationsLoading ? (
            <Box sx={{ p: 3, minHeight: 180, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
              <CircularProgress size={28} />
              <Typography color="text.secondary">
                Loading notifications...
              </Typography>
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <NotificationsNoneIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography color="text.secondary">
                No notifications yet
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {notifications.map((notification) => (
                <ListItem
                  key={notification.notification_id}
                  button
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: notification.is_read ? 'transparent' : 'action.hover',
                    '&:hover': {
                      bgcolor: 'action.selected'
                    }
                  }}
                >
                  <ListItemIcon>
                    {getNotificationIcon(notification)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: notification.is_read ? 'normal' : 'bold',
                            flex: 1
                          }}
                        >
                          {notification.title}
                        </Typography>
                        {!notification.is_read && (
                          <Chip 
                            size="small" 
                            label="New" 
                            color="primary" 
                            sx={{ height: 20 }}
                          />
                        )}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClearNotification(notification.notification_id);
                          }}
                          sx={{ 
                            ml: 1,
                            '&:hover': { bgcolor: 'error.main', color: 'white' }
                          }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    }
                    secondary={
                      <Box component="span">
                        <Box 
                          component="span"
                          sx={{ 
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            color: 'text.secondary',
                            fontSize: '0.875rem'
                          }}
                        >
                          {notification.message}
                        </Box>
                        <br />
                        <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          {formatNotificationTime(notification.created_date)}
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Menu>
    </>
  );
};

export default NotificationsDropdown;
